import Api from './api';

/**
 * Serviço para operações de Templates
 * Integração com TemplateController do backend
 */
class TemplateService {
  
  /**
   * Buscar todos os templates com paginação e filtros
   * @param {Object} params - Parâmetros de busca e paginação
   * @returns {Promise} Response com lista paginada de templates
   */
  static async getAll(params = {}) {
    const requestData = {
      page: params.page || 1,
      pageSize: params.pageSize || 10,
      Title: params.title || '',
      Id: params.id || null,
      TenantId: params.tenantId || null,
      sorterField: params.sorterField || '',
      sortOrder: params.sortOrder || 'desc'
    };

    return await Api.post('/template/getAll', requestData);
  }

  /**
   * Buscar template por ID
   * @param {string} id - ID do template
   * @returns {Promise} Response com dados do template
   */
  static async getById(id) {
    if (!id) {
      throw new Error('ID do template é obrigatório');
    }

    return await Api.get(`/template/getById/${id}`);
  }

  /**
   * Criar ou atualizar template (Upsert)
   * @param {Object} templateData - Dados do template
   * @returns {Promise} Response com template criado/atualizado
   */
  static async upsert(templateData) {
    try {
      
      if (!templateData.title || templateData.title.trim() === '') {
        throw new Error('Título do modelo é obrigatório');
      }

      // Validar se há widgets
      // Verificação removida ou ajustada para aceitar o que vem do frontend
      // O frontend já valida se tem widgets antes de chamar o upsert
      
      // Se templateData.schema já vier preenchido (string ou objeto), usamos ele
      // Se não, construímos o schema baseado nos outros campos
      
      let schemaToSend;
      
      if (templateData.schema) {
          // Se já tem schema, usamos ele. 
          // Se for string, fazemos parse para garantir que vamos encapsular num array se necessário
          let parsedSchema = typeof templateData.schema === 'string' 
            ? JSON.parse(templateData.schema) 
            : templateData.schema;
            
          // Garante que seja um array
          if (!Array.isArray(parsedSchema)) {
              parsedSchema = [parsedSchema];
          }
          schemaToSend = JSON.stringify(parsedSchema);
      } else {
          // Constrói o schema se não foi passado explicitamente
          const widgets = templateData.widgets || [];
          if (widgets.length === 0) {
            // Se não tem widgets e não tem schema, aí sim é erro
            throw new Error('O modelo deve ter pelo menos um gráfico.');
          }
          
          const templateSchema = {
            title: templateData.title.trim(),
            description: templateData.description || '',
            filters: templateData.filters || {},
            comparison: templateData.comparison || {},
            layout: templateData.layout || [],
            widgets: widgets,
            sync: templateData.sync || null,
            version: '1.0',
            createdAt: new Date().toISOString()
          };
          
          schemaToSend = JSON.stringify([templateSchema]);
      }

      const payload = {
        Id: templateData.id || '00000000-0000-0000-0000-000000000000',
        Title: templateData.title.trim(),
        Schema: schemaToSend,
        TenantId: templateData.tenantId
      };

      const response = await Api.post('/template/upsert', payload);
      return response;
    } catch (error) {
      console.error('❌ Erro ao salvar template:', error);
      throw error;
    }
  }

  /**
   * Deletar template
   * @param {string} id - ID do template
   * @returns {Promise} Response da operação de delete
   */
  static async delete(id) {
    try {
      if (!id) {
        throw new Error('ID do template é obrigatório');
      }

      
      const response = await Api.delete(`/template/${id}`);
      return response;
    } catch (error) {
      console.error('❌ Erro ao deletar template:', error);
      throw error;
    }
  }

  /**
   * Mapear dados do frontend para formato do backend
   * @param {Object} frontendData - Dados do Report Builder
   * @returns {Object} Dados formatados para o backend
   */
  static mapReportBuilderToTemplate(frontendData) {
    try {
      const templateSchema = {
        title: frontendData.title || 'Novo Relatório',
        description: frontendData.description || '',
        filters: frontendData.filters || {},
        comparison: frontendData.comparison || {},
        layout: frontendData.layout || [],
        widgets: frontendData.widgets || [],
        sync: frontendData.sync || null,
        version: '1.0',
        createdAt: new Date().toISOString()
      };

      
      return {
        title: frontendData.title || 'Novo Relatório',
        schema: JSON.stringify(templateSchema),
        tenantId: frontendData.tenantId || null,
        id: frontendData.id || null
      };
    } catch (error) {
      console.error('❌ Erro ao mapear dados do Report Builder:', error);
      throw new Error('Erro ao processar dados do relatório');
    }
  }

  /**
   * Mapear dados do backend para formato do frontend
   * @param {Object} backendData - Dados vindos do backend
   * @returns {Object} Dados formatados para o Report Builder
   */
  static mapTemplateToReportBuilder(backendData) {
    try {
      if (!backendData.schema) {
        throw new Error('Schema do template não encontrado');
      }
      
      let schema;
      
      // Verifica se o schema é um array (formato novo) ou string/objeto (formato antigo)
      if (Array.isArray(backendData.schema)) {
        // Se for array, pegamos o primeiro item que contém a estrutura do relatório
        // O backend pode estar retornando [ { title: ... } ]
        schema = backendData.schema[0];
      } else {
        // Comportamento padrão anterior
        schema = typeof backendData.schema === 'string' 
          ? JSON.parse(backendData.schema) 
          : backendData.schema;
          
        // Se após o parse virar array, pegamos o primeiro item
        if (Array.isArray(schema)) {
          schema = schema[0];
        }
      }

      if (!schema) {
         throw new Error('Schema inválido ou vazio');
      }

      return {
        id: backendData.id,
        title: schema.title || backendData.title || 'Template Carregado',
        description: schema.description || '',
        filters: schema.filters || {},
        comparison: schema.comparison || {},
        layout: schema.layout || [],
        widgets: schema.widgets || [],
        sync: schema.sync || null,
        tenantId: backendData.tenantId,
        version: schema.version || '1.0',
        createdAt: schema.createdAt || null
      };
    } catch (error) {
      console.error('❌ Erro ao mapear template para Report Builder:', error);
      console.error('❌ Dados recebidos:', backendData);
      throw new Error('Erro ao carregar template');
    }
  }

  /**
   * Buscar templates simplificados para selects
   * @param {Object} filters - Filtros opcionais
   * @returns {Promise} Lista simplificada de templates
   */
  static async getTemplatesForSelect(filters = {}) {
    try {
      const response = await this.getAll({ 
        page: 1, 
        pageSize: 100,
        ...filters 
      });
      
      const templates = response.data?.response?.data || [];
      
      return templates.map(template => ({
        id: template.id,
        title: template.title,
        tenantId: template.tenantId,
        value: template.id,
        label: template.title
      }));
    } catch (error) {
      console.error('❌ Erro ao buscar templates para select:', error);
      return [];
    }
  }
}

export default TemplateService; 